import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Table, TableBody,
  TableRow, TableCell, Chip, Grid, Collapse, ToggleButton,
  ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem,
  Pagination, Accordion, AccordionSummary, AccordionDetails, Checkbox,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { searchCalendar } from '../services/api';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import { sortFacetsBooleansFirst } from '../utils/facetOrder';

export default function CalendarSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'month');
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [showSearch, setShowSearch] = useState(searchParams.get('search') === 'true');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [facets, setFacets] = useState({});

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [dayEvents, setDayEvents] = useState([]);
  const [dayEventsLoading, setDayEventsLoading] = useState(false);

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    eventType: '',
    category: '',
    isSittingDay: '',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    if (showSearch) {
      const urlQuery = searchParams.get('q');
      if (urlQuery && urlQuery !== '*') {
        handleSearch(urlQuery);
      } else {
        handleSearch('*');
      }
    } else {
      setInitialLoading(false);
      loadCalendarEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showSearch) {
      loadCalendarEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, viewMode]);

  const loadCalendarEvents = async () => {
    setCalendarLoading(true);
    try {
      let dateFrom, dateTo;

      if (viewMode === 'week') {
        const startOfWeek = getStartOfWeek(currentDate);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        dateFrom = formatDateForQuery(startOfWeek);
        dateTo = formatDateForQuery(endOfWeek);
      } else if (viewMode === 'month') {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        dateFrom = formatDateForQuery(new Date(year, month, 1));
        dateTo = formatDateForQuery(new Date(year, month + 1, 0));
      } else if (viewMode === 'year') {
        const year = currentDate.getFullYear();
        dateFrom = `${year}-01-01`;
        dateTo = `${year}-12-31`;
      }

      const q = `date:>=${dateFrom} AND date:<=${dateTo}`;

      // Fetch all events with pagination (API limits to 100 per page)
      let allHits = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const data = await searchCalendar(q, page, 100);
        if (data.hits && data.hits.length > 0) {
          allHits = allHits.concat(data.hits);
          hasMore = allHits.length < data.total && page < 10; // Limit to 10 pages (1000 events max)
          page++;
        } else {
          hasMore = false;
        }
      }

      // Group events by date
      const eventsByDate = {};
      allHits.forEach(event => {
        const eventDate = event.date ? event.date.split('T')[0] : null;
        if (eventDate) {
          if (!eventsByDate[eventDate]) {
            eventsByDate[eventDate] = [];
          }
          eventsByDate[eventDate].push(event);
        }
      });
      setCalendarEvents(eventsByDate);
    } catch (err) {
      console.error('Failed to load calendar events:', err);
    }
    setCalendarLoading(false);
  };

  const formatDateForQuery = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  const handleDateClick = async (dateStr) => {
    setSelectedDate(dateStr);
    setDayDialogOpen(true);
    setDayEventsLoading(true);

    try {
      const q = `date:"${dateStr}"`;
      const data = await searchCalendar(q, 1, 100);
      setDayEvents(data.hits || []);
    } catch (err) {
      console.error('Failed to load day events:', err);
      setDayEvents([]);
    }
    setDayEventsLoading(false);
  };

  const navigateCalendar = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (viewMode === 'year') {
      newDate.setFullYear(newDate.getFullYear() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.eventType) parts.push(`detailed_type:"${form.eventType}"`);
    if (form.category) parts.push(`category:"${form.category}"`);
    if (form.isSittingDay !== '') parts.push(`is_sitting_day:${form.isSittingDay}`);
    if (form.dateFrom) parts.push(`date:>=${form.dateFrom}`);
    if (form.dateTo) parts.push(`date:<=${form.dateTo}`);

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, pageSize = perPage) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    const q = buildQueryWithFilters(baseQuery, filters);

    setLoading(true);
    setError(null);
    try {
      const data = await searchCalendar(q, newPage, pageSize);
      setResults(data);
      setPage(newPage);
      if (data.facets) {
        setFacets(data.facets);
      }
      const newParams = { q: baseQuery, mode: searchMode, search: 'true' };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setInitialLoading(false);
    setLoading(false);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const facetToField = (facetName) => {
    const mapping = {
      'event_types': 'detailed_type',
      'categories': 'category',
      'chambers': 'chamber',
      'sitting_days': 'is_sitting_day'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'event_types': 'Event Type',
      'categories': 'Category',
      'chambers': 'Chamber',
      'sitting_days': 'Sitting Day'
    };
    return names[facetName] || facetName;
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, newSize);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      eventType: '',
      category: '',
      isSittingDay: '',
      dateFrom: '',
      dateTo: ''
    });
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSearchParams({});
  };

  const toggleDetails = (id) => {
    setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const truncateContent = (content, maxLength = 300) => {
    if (!content) return '';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const getEventTypeColor = (type) => {
    if (!type) return 'default';
    const lowerType = type.toLowerCase();
    if (lowerType.includes('sitting')) return 'primary';
    if (lowerType.includes('tour')) return 'info';
    if (lowerType.includes('exhibition')) return 'secondary';
    if (lowerType.includes('committee')) return 'warning';
    if (lowerType.includes('event')) return 'success';
    return 'default';
  };

  const MetadataRow = ({ label, value }) => {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return null;
    return (
      <TableRow>
        <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '30%', py: 1 }}>{label}</TableCell>
        <TableCell sx={{ py: 1 }}>{Array.isArray(value) ? value.join(', ') : String(value)}</TableCell>
      </TableRow>
    );
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const getCalendarTitle = () => {
    if (viewMode === 'week') {
      const startOfWeek = getStartOfWeek(currentDate);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      return `${startOfWeek.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (viewMode === 'month') {
      return currentDate.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
    } else {
      return currentDate.getFullYear().toString();
    }
  };

  // Render calendar grid
  const renderCalendar = () => {
    if (viewMode === 'week') {
      return renderWeekView();
    } else if (viewMode === 'month') {
      return renderMonthView();
    } else {
      return renderYearView();
    }
  };

  const renderWeekView = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(day.getDate() + i);
      days.push(day);
    }

    return (
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        {days.map((day, idx) => {
          const dateStr = formatDateForQuery(day);
          const events = calendarEvents[dateStr] || [];
          const isToday = formatDateForQuery(new Date()) === dateStr;

          return (
            <Paper
              key={idx}
              sx={{
                flex: 1,
                p: 1,
                minHeight: 300,
                bgcolor: isToday ? '#fff3e0' : 'white',
                border: isToday ? '2px solid #ff9800' : '1px solid #e0e0e0',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
              onClick={() => handleDateClick(dateStr)}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: 'center',
                  fontWeight: isToday ? 700 : 500,
                  color: isToday ? '#e65100' : 'text.primary',
                  mb: 1
                }}
              >
                {day.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' })}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {events.slice(0, 5).map((event, eventIdx) => (
                <Chip
                  key={eventIdx}
                  label={event.title || event.detailed_type || 'Event'}
                  size="small"
                  color={getEventTypeColor(event.detailed_type)}
                  sx={{ mb: 0.5, width: '100%', justifyContent: 'flex-start', fontSize: '0.7rem' }}
                />
              ))}
              {events.length > 5 && (
                <Typography variant="caption" color="text.secondary">
                  +{events.length - 5} more
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    );
  };

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay() || 7; // Convert Sunday (0) to 7
    const daysInMonth = lastDay.getDate();

    const weeks = [];
    let currentWeek = [];

    // Add empty cells for days before first of month
    for (let i = 1; i < startingDay; i++) {
      currentWeek.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Add empty cells for remaining days
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={0.5}>
          {dayNames.map((name, idx) => (
            <Grid item xs={12/7} key={idx}>
              <Box sx={{ textAlign: 'center', py: 1, fontWeight: 600, color: 'text.secondary' }}>
                {name}
              </Box>
            </Grid>
          ))}
          {weeks.map((week, weekIdx) => (
            <React.Fragment key={weekIdx}>
              {week.map((day, dayIdx) => {
                if (day === null) {
                  return (
                    <Grid item xs={12/7} key={dayIdx}>
                      <Box sx={{ minHeight: 100, bgcolor: '#fafafa' }} />
                    </Grid>
                  );
                }

                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const events = calendarEvents[dateStr] || [];
                const isToday = formatDateForQuery(new Date()) === dateStr;

                return (
                  <Grid item xs={12/7} key={dayIdx}>
                    <Paper
                      sx={{
                        minHeight: 100,
                        p: 0.5,
                        bgcolor: isToday ? '#fff3e0' : 'white',
                        border: isToday ? '2px solid #ff9800' : '1px solid #e0e0e0',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' }
                      }}
                      onClick={() => handleDateClick(dateStr)}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isToday ? 700 : 400,
                          color: isToday ? '#e65100' : 'text.primary',
                          mb: 0.5
                        }}
                      >
                        {day}
                      </Typography>
                      {events.slice(0, 3).map((event, eventIdx) => (
                        <Box
                          key={eventIdx}
                          sx={{
                            bgcolor: event.is_sitting_day ? '#e3f2fd' : '#f3e5f5',
                            borderRadius: 0.5,
                            px: 0.5,
                            mb: 0.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                            {event.title || event.detailed_type || 'Event'}
                          </Typography>
                        </Box>
                      ))}
                      {events.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          +{events.length - 3} more
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </React.Fragment>
          ))}
        </Grid>
      </Box>
    );
  };

  const getEventTypesForDate = (dateStr) => {
    const events = calendarEvents[dateStr] || [];
    const types = new Set();
    events.forEach(event => {
      const type = event.detailed_type?.toLowerCase() || 'other';
      if (type.includes('sitting')) types.add('sitting');
      else if (type.includes('committee')) types.add('committee');
      else if (type.includes('tour')) types.add('tour');
      else if (type.includes('exhibition')) types.add('exhibition');
      else types.add('event');
    });
    return Array.from(types);
  };

  const getEventDotColor = (type) => {
    switch (type) {
      case 'sitting': return '#1976d2'; // blue
      case 'committee': return '#ed6c02'; // orange
      case 'tour': return '#0288d1'; // light blue
      case 'exhibition': return '#9c27b0'; // purple
      case 'event': return '#2e7d32'; // green
      default: return '#757575'; // grey
    }
  };

  const renderMiniMonth = (monthIndex) => {
    const year = currentDate.getFullYear();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const startingDay = firstDay.getDay() || 7; // Convert Sunday (0) to 7 for Monday start
    const daysInMonth = lastDay.getDate();
    const monthName = firstDay.toLocaleDateString('en-AU', { month: 'long' });

    const weeks = [];
    let currentWeek = [];

    // Add empty cells for days before first of month
    for (let i = 1; i < startingDay; i++) {
      currentWeek.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Add empty cells for remaining days
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    const dayNames = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    const todayStr = formatDateForQuery(new Date());

    return (
      <Paper
        elevation={1}
        sx={{
          p: 1.5,
          height: '100%',
          borderRadius: 2,
          '&:hover': { boxShadow: 3 },
          transition: 'box-shadow 0.2s'
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 1,
            textAlign: 'center',
            color: '#e91e63'
          }}
        >
          {monthName}
        </Typography>

        {/* Day headers */}
        <Box sx={{ display: 'flex', mb: 0.5 }}>
          {dayNames.map((name, idx) => (
            <Box
              key={idx}
              sx={{
                flex: 1,
                textAlign: 'center',
                fontSize: '0.65rem',
                fontWeight: 600,
                color: 'text.secondary'
              }}
            >
              {name}
            </Box>
          ))}
        </Box>

        {/* Calendar grid */}
        {weeks.map((week, weekIdx) => (
          <Box key={weekIdx} sx={{ display: 'flex' }}>
            {week.map((day, dayIdx) => {
              if (day === null) {
                return <Box key={dayIdx} sx={{ flex: 1, height: 28 }} />;
              }

              const dateStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const eventTypes = getEventTypesForDate(dateStr);
              const isToday = todayStr === dateStr;
              const hasEvents = eventTypes.length > 0;

              return (
                <Box
                  key={dayIdx}
                  onClick={() => handleDateClick(dateStr)}
                  sx={{
                    flex: 1,
                    height: 28,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: hasEvents ? 'pointer' : 'default',
                    borderRadius: 1,
                    bgcolor: isToday ? '#fff3e0' : 'transparent',
                    border: isToday ? '1px solid #ff9800' : '1px solid transparent',
                    '&:hover': hasEvents ? { bgcolor: '#f5f5f5' } : {}
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: isToday ? 700 : 400,
                      color: isToday ? '#e65100' : 'text.primary',
                      lineHeight: 1
                    }}
                  >
                    {day}
                  </Typography>
                  {/* Event type dots */}
                  {hasEvents && (
                    <Box sx={{ display: 'flex', gap: '2px', mt: '1px' }}>
                      {eventTypes.slice(0, 3).map((type, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: getEventDotColor(type)
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Paper>
    );
  };

  const renderYearView = () => {
    // Two columns with 6 months each
    const leftColumnMonths = [0, 1, 2, 3, 4, 5]; // Jan - Jun
    const rightColumnMonths = [6, 7, 8, 9, 10, 11]; // Jul - Dec

    return (
      <Box sx={{ mt: 2 }}>
        {/* Legend */}
        <Paper elevation={1} sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mr: 1 }}>Event Types:</Typography>
            {[
              { type: 'sitting', label: 'Sitting' },
              { type: 'committee', label: 'Committee' },
              { type: 'tour', label: 'Tour' },
              { type: 'exhibition', label: 'Exhibition' },
              { type: 'event', label: 'Event' }
            ].map(({ type, label }) => (
              <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: getEventDotColor(type)
                  }}
                />
                <Typography variant="caption">{label}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Grid container spacing={2}>
          {/* Left Column - Jan to Jun */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {leftColumnMonths.map((monthIndex) => (
                <Box key={monthIndex}>
                  {renderMiniMonth(monthIndex)}
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Right Column - Jul to Dec */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {rightColumnMonths.map((monthIndex) => (
                <Box key={monthIndex}>
                  {renderMiniMonth(monthIndex)}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon sx={{ color: '#e91e63', fontSize: 32 }} />
          <Typography variant="h4">Parliament Calendar</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={showSearch ? 'outlined' : 'contained'}
            onClick={() => {
              setShowSearch(false);
              setSearchParams({});
            }}
            startIcon={<CalendarViewMonthIcon />}
            sx={{ borderRadius: 2 }}
          >
            Calendar
          </Button>
          <Button
            variant={showSearch ? 'contained' : 'outlined'}
            onClick={() => {
              setShowSearch(true);
              handleSearch('*');
            }}
            startIcon={<SearchIcon />}
            sx={{ borderRadius: 2 }}
          >
            Search
          </Button>
        </Box>
      </Box>

      {/* Calendar View */}
      {!showSearch && (
        <Box>
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={() => navigateCalendar(-1)} size="small">
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
                  {getCalendarTitle()}
                </Typography>
                <IconButton onClick={() => navigateCalendar(1)} size="small">
                  <ChevronRightIcon />
                </IconButton>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={goToToday}
                  startIcon={<TodayIcon />}
                  sx={{ ml: 2 }}
                >
                  Today
                </Button>
              </Box>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="week">Week</ToggleButton>
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="year">Year</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Paper>

          {calendarLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderCalendar()
          )}

          {/* Day Events Dialog */}
          <Dialog
            open={dayDialogOpen}
            onClose={() => setDayDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Events on {selectedDate && formatDate(selectedDate)}
            </DialogTitle>
            <DialogContent>
              {dayEventsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : dayEvents.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No events on this date.
                </Typography>
              ) : (
                <List>
                  {dayEvents.map((event, idx) => (
                    <React.Fragment key={event.id || idx}>
                      {idx > 0 && <Divider />}
                      <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {event.title || 'Untitled Event'}
                              </Typography>
                              {event.detailed_type && (
                                <Chip
                                  label={event.detailed_type}
                                  size="small"
                                  color={getEventTypeColor(event.detailed_type)}
                                />
                              )}
                              {event.is_sitting_day && (
                                <Chip label="Sitting Day" size="small" variant="outlined" color="primary" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {event.venue && (
                                <Typography variant="body2" color="text.secondary">
                                  Venue: {event.venue}
                                </Typography>
                              )}
                              {event.summary && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {truncateContent(event.summary, 200)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDayDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Search View */}
      {showSearch && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <ToggleButtonGroup
              value={searchMode}
              exclusive
              onChange={handleModeChange}
              size="small"
            >
              <ToggleButton value="simple">
                <SearchIcon sx={{ mr: 1 }} /> Simple
              </ToggleButton>
              <ToggleButton value="advanced">
                <TuneIcon sx={{ mr: 1 }} /> Advanced
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Simple Search */}
          <Collapse in={searchMode === 'simple'}>
            <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search events, exhibitions, tours..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 3, px: 4, bgcolor: '#e91e63', '&:hover': { bgcolor: '#c2185b' } }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
                  Reset
                </Button>
              </Box>
            </Paper>
          </Collapse>

          {/* Advanced Search */}
          <Collapse in={searchMode === 'advanced'}>
            <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Keywords"
                    value={form.keywords}
                    onChange={handleChange('keywords')}
                    placeholder="Search in event titles and content"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Event Type</InputLabel>
                    <Select value={form.eventType} onChange={handleChange('eventType')} label="Event Type">
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="Tour">Tour</MenuItem>
                      <MenuItem value="Exhibition">Exhibition</MenuItem>
                      <MenuItem value="Event">Event</MenuItem>
                      <MenuItem value="Sitting">Sitting</MenuItem>
                      <MenuItem value="Committee">Committee</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select value={form.category} onChange={handleChange('category')} label="Category">
                      <MenuItem value="">All Categories</MenuItem>
                      <MenuItem value="school">School</MenuItem>
                      <MenuItem value="holiday">Holiday</MenuItem>
                      <MenuItem value="public">Public</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sitting Day</InputLabel>
                    <Select value={form.isSittingDay} onChange={handleChange('isSittingDay')} label="Sitting Day">
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="true">Sitting Days Only</MenuItem>
                      <MenuItem value="false">Non-Sitting Days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Date From"
                    type="date"
                    value={form.dateFrom}
                    onChange={handleChange('dateFrom')}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Date To"
                    type="date"
                    value={form.dateTo}
                    onChange={handleChange('dateTo')}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleSearch()}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                      sx={{ borderRadius: 2, bgcolor: '#e91e63', '&:hover': { bgcolor: '#c2185b' } }}
                    >
                      Search
                    </Button>
                    <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                      Reset
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Collapse>

          {/* Loading skeleton for initial load */}
          {initialLoading && <SearchSkeleton count={5} showFacets={true} />}

          {/* Error state */}
          {!initialLoading && error && (
            <ErrorState
              message={error}
              onRetry={() => handleSearch()}
              themeColor="#e91e63"
            />
          )}

          {/* Empty state when no results */}
          {!initialLoading && !error && results && results.total === 0 && (
            <EmptyState
              query={query}
              type="calendar"
              onSuggestionClick={(q) => {
                setQuery(q);
                handleSearch(q);
              }}
              onReset={handleReset}
              themeColor="#e91e63"
            />
          )}

          {/* Results display */}
          {!initialLoading && !error && results && results.total > 0 && (
            <Grid container spacing={3}>
              {/* Facets Panel */}
              {Object.keys(facets).length > 0 && (
                <Grid item xs={12} md={3}>
                  <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                        Filter Results
                      </Typography>
                      {Object.keys(activeFilters).length > 0 && (
                        <Button size="small" onClick={clearAllFilters}>Clear All</Button>
                      )}
                    </Box>

                    {sortFacetsBooleansFirst(['event_types', 'categories', 'sitting_days'], facets).map((facetName) => (
                      facets[facetName] && facets[facetName].buckets && facets[facetName].buckets.length > 0 && (
                        <Accordion key={facetName} defaultExpanded={true}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">
                              {facetDisplayName(facetName)}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <List dense sx={{ py: 0 }}>
                              {facets[facetName].buckets.slice(0, 15).map((bucket) => (
                                <ListItem key={bucket.key} disablePadding>
                                  <Checkbox
                                    size="small"
                                    checked={activeFilters[facetToField(facetName)]?.includes(String(bucket.key)) || false}
                                    onChange={() => handleFacetClick(facetToField(facetName), String(bucket.key))}
                                  />
                                  <ListItemText
                                    primary={`${facetName === 'sitting_days' ? (bucket.key === 1 || bucket.key === true ? 'Yes' : 'No') : bucket.key} (${bucket.doc_count.toLocaleString()})`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </AccordionDetails>
                        </Accordion>
                      )
                    ))}
                  </Paper>
                </Grid>
              )}

              {/* Results */}
              <Grid item xs={12} md={Object.keys(facets).length > 0 ? 9 : 12}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="subtitle1">
                        {results.total.toLocaleString()} events found
                      </Typography>
                      <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                    </Box>
                    {results.total > perPage && (
                      <Pagination
                        count={Math.ceil(results.total / perPage)}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        size="small"
                      />
                    )}
                  </Box>
                  <List>
                    {results.hits.map((hit, index) => (
                      <React.Fragment key={hit.id}>
                        {index > 0 && <Divider />}
                        <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="resultTitle">
                                  {hit.title || 'Untitled Event'}
                                </Typography>
                                {hit.detailed_type && (
                                  <Chip
                                    label={hit.detailed_type}
                                    size="small"
                                    color={getEventTypeColor(hit.detailed_type)}
                                  />
                                )}
                                {hit.is_sitting_day && (
                                  <Chip
                                    label="Sitting Day"
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  {formatDate(hit.date)}
                                  {hit.date_from && hit.date_to && hit.date_from !== hit.date_to && (
                                    <> ({formatDateTime(hit.date_from)} - {formatDateTime(hit.date_to)})</>
                                  )}
                                </Typography>
                                {hit.venue && (
                                  <Typography variant="body2" color="text.secondary">
                                    Venue: {hit.venue}
                                  </Typography>
                                )}
                                <Typography className="ps-snippet" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  {truncateContent(hit.summary || hit.content)}
                                </Typography>
                              </Box>
                            }
                          />
                          <Button size="small" onClick={() => toggleDetails(hit.id)} sx={{ mt: 1 }}>
                            {expandedDetails[hit.id] ? 'Hide details' : 'Show all details'}
                          </Button>
                          <Collapse in={expandedDetails[hit.id]}>
                            <Box sx={{ mt: 2, width: '100%' }}>
                              <Table size="small">
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={2} sx={{ bgcolor: '#fce4ec', fontWeight: 600 }}>
                                      Event Information
                                    </TableCell>
                                  </TableRow>
                                  <MetadataRow label="Title" value={hit.title} />
                                  <MetadataRow label="Event Type" value={hit.detailed_type} />
                                  <MetadataRow label="Category" value={hit.category} />
                                  <MetadataRow label="Date" value={formatDate(hit.date)} />
                                  <MetadataRow label="From" value={formatDateTime(hit.date_from)} />
                                  <MetadataRow label="To" value={formatDateTime(hit.date_to)} />
                                  <MetadataRow label="Venue" value={hit.venue} />
                                  <MetadataRow label="Chamber" value={hit.chamber} />
                                  <MetadataRow label="Sitting Day" value={hit.is_sitting_day ? 'Yes' : 'No'} />
                                </TableBody>
                              </Table>
                              {hit.content && (
                                <Paper sx={{ p: 2, mt: 2, bgcolor: '#fafafa', maxHeight: 400, overflow: 'auto' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Description
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {hit.content}
                                  </Typography>
                                </Paper>
                              )}
                              {hit.source_url && (
                                <Box sx={{ mt: 2 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    href={hit.source_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    View on APH Website
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                  {results.total > perPage && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={Math.ceil(results.total / perPage)}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}
